import { consoleRoutes, WEBHOOK_EVENTS, type WebhookEventKey } from "@sagot/constants";
import { useTranslation } from "@sagot/i18n";
import { AppLink, Badge, Checkbox, Segmented, Skeleton } from "@sagot/ui";
import { usePermissions } from "../../../../../session/permissions";
import { useWorkspaceContext } from "../../../../../session/workspace-context";
import type { TriggerOptions } from "../../types";
import { useEndpoints } from "../../use-workflows";

const E = "engage.workflows.defaults.events";

interface PlatformEventOptionsProps {
  options: TriggerOptions;
  disabled: boolean;
  onChange: (options: TriggerOptions) => void;
}

/** The events the endpoints currently subscribe to (every enabled endpoint that is not "only from my workflows"), in the catalogue's order. */
function subscribedEvents(endpoints: readonly { enabled: boolean; delivery_mode: string; events: string[] }[]): WebhookEventKey[] {
  const named = new Set(endpoints.filter((endpoint) => endpoint.enabled && endpoint.delivery_mode !== "workflows").flatMap((endpoint) => endpoint.events));
  return WEBHOOK_EVENTS.filter((event) => named.has(event));
}

function Subscribed() {
  const { t } = useTranslation();
  const { summary } = useWorkspaceContext();
  const canRead = usePermissions().can("webhooks.manage");
  const endpoints = useEndpoints(canRead);
  if (!canRead) return <p className="text-xs text-fg-subtle">{t(`${E}.noAccess`)}</p>;
  if (endpoints.isPending) return <Skeleton className="h-6 w-64 max-w-full" aria-hidden="true" />;
  if (endpoints.isError) return <p className="text-xs text-fg-subtle">{t(`${E}.loadFailed`)}</p>;
  const events = subscribedEvents(endpoints.data);
  return (
    <div className="space-y-1.5" data-testid="subscribed-events">
      <p className="text-xs font-semibold text-fg-muted">{t(`${E}.subscribed`)}</p>
      {events.length > 0 ? (
        <ul className="flex flex-wrap gap-1.5">
          {events.map((event) => <li key={event}><Badge tone="brand" className="font-mono">{event}</Badge></li>)}
        </ul>
      ) : (
        <p className="text-xs text-fg-subtle">{t(`${E}.none`)}</p>
      )}
      <AppLink className="text-xs font-semibold text-primary-ink underline" href={consoleRoutes.settings(summary.slug, "webhooks")}>{t(`${E}.link`)}</AppLink>
    </div>
  );
}

/** The Developer webhooks default's trigger: every event the endpoints subscribe to, or only some. Each endpoint still chooses its own events. */
export function PlatformEventOptions({ options, disabled, onChange }: PlatformEventOptionsProps) {
  const { t } = useTranslation();
  const events = options.events ?? "all";
  const chosen = events === "all" ? [] : events;
  const toggle = (event: WebhookEventKey, on: boolean) => {
    const next = WEBHOOK_EVENTS.filter((key) => (key === event ? on : chosen.includes(key)));
    onChange({ ...options, events: next });
  };
  return (
    <div className="space-y-4" data-testid="platform-event-options">
      <div>
        <span className="label">{t(`${E}.mode`)}</span>
        <Segmented
          aria-label={t(`${E}.mode`)}
          value={events === "all" ? "all" : "only"}
          onValueChange={(mode) => { onChange({ ...options, events: mode === "all" ? "all" : WEBHOOK_EVENTS.filter((event) => chosen.includes(event)) }); }}
          options={[{ value: "all", label: t(`${E}.all`) }, { value: "only", label: t(`${E}.only`) }]}
        />
        <p className="mt-1.5 text-xs text-fg-subtle">{t(`${E}.hint`)}</p>
      </div>
      {events === "all" ? null : (
        <fieldset>
          <legend className="label">{t(`${E}.only`)}</legend>
          <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
            {WEBHOOK_EVENTS.map((event) => (
              <div key={event} className="rounded-md border border-line px-3 py-2">
                <Checkbox checked={chosen.includes(event)} disabled={disabled} onCheckedChange={(checked) => { toggle(event, checked === true); }} label={<span className="font-mono text-xs">{event}</span>} />
              </div>
            ))}
          </div>
          {chosen.length === 0 ? <p role="alert" className="mt-1.5 text-xs font-semibold text-danger">{t(`${E}.required`)}</p> : null}
        </fieldset>
      )}
      <Subscribed />
    </div>
  );
}
